import React, { forwardRef, useEffect, useRef } from 'react';
import { PopupWindow as StyledPopupWindow } from '../../Components/globalStyles';
import { Icon } from '../../Universal/NovusDSImports';
import { STRINGS } from '../../Constants/ConstantStrings';
import { TooltipComponent } from '../Tooltip';

interface PopupWindowContainerProps {
  title: string;
  onClose: () => void;
  closeAriaLabel?: string;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  iconWidth?: string;
  iconHeight?: string;
  style?: React.CSSProperties;
  props?: React.HTMLProps<HTMLDivElement>;
  id?: string;
  dataTestId?: string;
}

const PopupWindowContainer = forwardRef<
  HTMLDivElement,
  PopupWindowContainerProps
>(
  (
    {
      title,
      onClose,
      closeAriaLabel = 'Close popup window',
      children,
      className,
      bodyClassName,
      iconWidth = '16px',
      iconHeight = '16px',
      style,
      id,
      dataTestId
    },
    ref
  ) => {
    const headingRef = useRef<HTMLHeadingElement>(null);
    const [enableTooltip, setEnableTooltip] = React.useState(false);

    useEffect(() => {
      if (headingRef.current) {
        headingRef.current.focus();
      }

      const tooltipTimer = setTimeout(() => {
        setEnableTooltip(true);
      }, 500);

      return () => clearTimeout(tooltipTimer);
    }, []);

    return (
      <StyledPopupWindow
        ref={ref}
        className={className}
        style={style}
        id={id}
        data-testid={dataTestId}
      >
        <div className="header">
          <h5 ref={headingRef} tabIndex={0}>
            {title}
          </h5>
          <div
            role="button"
            data-tooltip-id={enableTooltip ? 'close-popup-window' : undefined}
            aria-label={closeAriaLabel}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClose();
              }
            }}
            className={`close-icon ${className}`}
          >
            <Icon
              tabIndex={0}
              icon="close"
              width={iconWidth}
              height={iconHeight}
            >
              {STRINGS.CLOSE}
            </Icon>
          </div>
        </div>
        <div className={`body ${bodyClassName || ''}`}>{children}</div>
        <TooltipComponent
          id={`close-popup-window`}
          place={'bottom-end'}
          noArrow
        >
          {STRINGS.CLOSE}
        </TooltipComponent>
      </StyledPopupWindow>
    );
  }
);

PopupWindowContainer.displayName = 'PopupWindowContainer';

export default PopupWindowContainer;
